/**
 * Reprendre une partie là où on l'a laissée.
 *
 * ## Ce qui manquait
 *
 * `shared/sauvegarde.ts` prouve depuis le lot 4A qu'une `Partie` se sérialise et se
 * relit intacte — jet en cours, rampeau et résolutions partielles compris. C'était la
 * moitié du chemin : personne ne l'appelait, et le § 5.4 demande pourtant de
 * **sauvegarder la partie solo** dès que l'application passe en arrière-plan.
 *
 * L'autre moitié, c'est ce qu'il faut retenir **autour** de la cave. Une cave seule ne
 * suffit pas à reprendre une soirée — il manquerait la table, l'étape et les perdants
 * — ni un comptoir de campagne, où il faut en plus savoir de quel lieu il s'agit et ce
 * que le relevé a déjà compté. On sauve donc le `Tournoi` entier et son contexte.
 *
 * ## Refuser plutôt que deviner
 *
 * Une reprise à moitié comprise est pire qu'une reprise absente : le joueur retrouve
 * une partie qui a l'air de la sienne et qui ne l'est pas. Format inconnu, autres
 * règles, partie déjà finie, comptoir retiré depuis une mise à jour du contenu — dans
 * tous ces cas on rend `null` et on repart d'un menu propre. C'est la même sévérité
 * que `deserialiser`, pour la même raison.
 */

import {
  COMPTOIRS, deserialiser, terminee, VERSION_SAUVEGARDE,
  type Statistiques, type Tournoi,
} from "@zinc421/shared";

import type { Textes } from "../i18n/index.js";

/** L'identifiant du jeu de règles en vigueur, celui des tables embarquées. */
const REGLES_EN_VIGUEUR = "maison-2026-07";

/** Une partie en cours, avec tout ce qu'il faut pour la rouvrir telle quelle. */
export interface EnCours {
  /** Soirée libre, ou comptoir de campagne. */
  readonly quoi: "soiree" | "comptoir";
  /** Numéro du comptoir, pour une campagne seulement. */
  readonly comptoir?: number;
  readonly tournoi: Tournoi;
  /** Le relevé des caves déjà jouées ici — une soirée en compte trois. */
  readonly releve: Statistiques;
}

/**
 * Version du format de reprise.
 *
 * Distincte de `VERSION_SAUVEGARDE`, qui porte sur la forme d'une `Partie` : les deux
 * peuvent bouger séparément, et confondre les deux ferait jeter des reprises valides
 * à chaque changement d'enveloppe.
 */
export const VERSION_REPRISE = 1;

interface Enveloppe {
  readonly version: number;
  readonly versionPartie: number;
  readonly quoi: EnCours["quoi"];
  readonly comptoir?: number;
  readonly tournoi: Tournoi;
  readonly releve: Statistiques;
}

export function serialiserReprise(enCours: EnCours): string {
  const enveloppe: Enveloppe = {
    version: VERSION_REPRISE,
    versionPartie: VERSION_SAUVEGARDE,
    quoi: enCours.quoi,
    ...(enCours.comptoir === undefined ? {} : { comptoir: enCours.comptoir }),
    tournoi: enCours.tournoi,
    releve: enCours.releve,
  };
  return JSON.stringify(enveloppe);
}

/**
 * Relit une partie en cours, ou `null` si rien n'est sûr.
 *
 * Ne lève jamais : l'appelant est un écran d'accueil, et une exception y ferait
 * disparaître le menu pour une sauvegarde périmée.
 */
export function deserialiserReprise(brut: string): EnCours | null {
  let enveloppe: Enveloppe;
  try {
    enveloppe = JSON.parse(brut) as Enveloppe;
  } catch {
    return null;
  }
  if (!enveloppe || typeof enveloppe !== "object") return null;
  if (enveloppe.version !== VERSION_REPRISE) return null;
  if (enveloppe.versionPartie !== VERSION_SAUVEGARDE) return null;
  if (enveloppe.quoi !== "soiree" && enveloppe.quoi !== "comptoir") return null;

  const tournoi = enveloppe.tournoi;
  if (!tournoi || typeof tournoi !== "object") return null;
  if (!Array.isArray(tournoi.table) || tournoi.table.length < 2) return null;
  if (!Array.isArray(tournoi.participants)) return null;
  if (tournoi.format !== "SIMPLE" && tournoi.format !== "SOIREE") return null;
  if (tournoi.etape === "FINI") return null;

  // La partie repasse par le contrôle du lot 4A : même sévérité sur la forme, et le
  // même refus si elle vient d'un autre jeu de règles. Les tables d'IA y sont
  // calibrées — une partie reprise sous d'autres règles serait jouable en apparence
  // et fausse en pratique.
  let partie;
  try {
    partie = deserialiser(
      JSON.stringify({
        version: VERSION_SAUVEGARDE,
        ruleSetId: tournoi.partie?.regles?.id,
        partie: tournoi.partie,
      }),
      REGLES_EN_VIGUEUR);
  } catch {
    return null;
  }
  // Reprendre un podium n'a pas de sens, et le proposer ferait douter le joueur de
  // ce qu'il vient de gagner.
  if (terminee(partie)) return null;

  if (enveloppe.quoi === "comptoir") {
    const numero = enveloppe.comptoir;
    if (typeof numero !== "number") return null;
    // Le contenu de la campagne bouge : une reprise peut désigner un comptoir retiré
    // depuis. Mieux vaut un menu propre qu'un plantage à l'ouverture.
    if (!COMPTOIRS.some((c) => c.numero === numero)) return null;
  }

  return {
    quoi: enveloppe.quoi,
    ...(enveloppe.comptoir === undefined ? {} : { comptoir: enveloppe.comptoir }),
    tournoi: { ...tournoi, partie },
    releve: enveloppe.releve,
  };
}

/** De quoi annoncer la reprise au joueur, en une ligne. */
export function libelleReprise(enCours: EnCours, textes: Textes): string {
  const comptoir = enCours.comptoir === undefined
    ? null : COMPTOIRS.find((c) => c.numero === enCours.comptoir);
  const manche = enCours.tournoi.partie.manche?.numero ?? 1;
  const ou = comptoir ? textes.comptoirNom(comptoir.cle) : textes.repriseSoiree;
  return `${ou} — ${textes.repriseManche(manche)}`;
}
